import type { ReactElement } from 'react';
import { useFieldArray } from 'react-hook-form';
import type { ChannelDto } from '@gr/core/distribution/channels/dto';
import { ErrorCode } from '@gr/core/errors';
import { StepPlacement, type SaveSchemeRequestDto, type SchemeDto } from '@gr/core/distribution/scheduling/dto';
import { useT } from '../../../../app/di';
import { Button, IconButton } from '../../../../ui-kit/Button/Button';
import { FieldRow, FieldRowActions, FieldRowGrow } from '../../../../ui-kit/FieldRow/FieldRow';
import { FormAlert, FormSelectField, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { nextStep, schemeFormValues, toSchemeRequest, type SchemeValues } from '../model/scheme-form';
import { useChannelOptions } from '../../channels';
import styles from './SchemeForm.module.scss';

export interface SchemeFormProps {
  id: string;
  scheme: SchemeDto | null;
  /** Активные каналы блога — куда может идти шаг. */
  channels: readonly ChannelDto[];
  onSubmit(input: SaveSchemeRequestDto): Promise<ServerOutcome>;
}

/** SCH-UC-1: название и шаги схемы. Строки шагов — useFieldArray, ключ строки — field.id (SYS-UC-12). */
export function SchemeForm({ id, scheme, channels, onSubmit }: SchemeFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<SchemeValues>({ defaultValues: schemeFormValues(scheme, channels), fields: ['name', 'steps'], onSubmit: (values) => onSubmit(toSchemeRequest(values)) });
  const { control, getValues, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'steps' });
  // CHN-UC-6: платформа канала — в подписи варианта.
  const channelOptions = useChannelOptions(channels);
  const disabled = formState.isSubmitting;
  return (
    <form id={id} className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <FormTextField control={control} name="name" label={t('scheduling.schemes.name')} rules={{ required: ErrorCode.FieldRequired }} disabled={disabled} />
      <h3>{t('scheduling.schemes.steps')}</h3>
      {fields.map((field, index) => (
        <fieldset key={field.id} className={styles.step}>
          <legend className={styles.legend}>{t('scheduling.schemes.step', { n: index + 1 })}</legend>
          <FormSelectField control={control} name={`steps.${index}.channelId`} label={t('scheduling.schemes.channel')} options={channelOptions} disabled={disabled} />
          {index > 0 ? (
            <FieldRow from="sm">
              <FieldRowGrow>
                <FormTextField control={control} name={`steps.${index}.delayHours`} label={t('scheduling.schemes.delay')} type="number" min={0} step={0.5} inputMode="decimal" disabled={disabled} />
              </FieldRowGrow>
              <FieldRowGrow>
                <FormSelectField
                  control={control}
                  name={`steps.${index}.placement`}
                  label={t('scheduling.schemes.placement')}
                  options={Object.values(StepPlacement).map((p) => ({ value: p, label: t(`scheduling.schemes.placements.${p}`) }))}
                  disabled={disabled}
                />
              </FieldRowGrow>
              <FieldRowActions>
                <IconButton icon="trash" label={t('scheduling.schemes.removeStep')} disabled={disabled} onClick={() => remove(index)} />
              </FieldRowActions>
            </FieldRow>
          ) : null}
        </fieldset>
      ))}
      <div>
        <Button icon="plus" disabled={disabled || fields.length >= channels.length} onClick={() => append(nextStep(getValues('steps'), channels))}>
          {t('scheduling.schemes.addStep')}
        </Button>
      </div>
    </form>
  );
}
